import { useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { PageHeader } from '@/app/layouts/PageHeader'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { UserListItem, useFollowers, useFollowing } from '@/features/profile'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'

export function FollowListPage({ kind }: { kind: 'followers' | 'following' }) {
  const t = useT()
  const { username = '' } = useParams()
  const followers = useFollowers(username)
  const following = useFollowing(username)
  const query = kind === 'followers' ? followers : following

  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  const title = kind === 'followers' ? t('profile.followers') : t('profile.following')
  const users = query.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <>
      <PageHeader title={title} subtitle={`@${username}`} back />

      {query.isPending && <PostListSkeleton count={4} />}
      {query.isError &&
        (query.error instanceof ApiError && query.error.code === 'protected' ? (
          <EmptyState
            title={t('protected.listTitle')}
            description={t('protected.postsBody', { username })}
          />
        ) : (
          <ErrorState message={query.error.message} onRetry={() => query.refetch()} />
        ))}

      {!query.isPending && !query.isError && users.length === 0 && (
        <EmptyState
          title={kind === 'followers' ? t('profile.noFollowers') : t('profile.noFollowing')}
        />
      )}

      {users.map((user) => (
        <UserListItem key={user.id} user={user} />
      ))}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}
